import { Link } from "react-router-dom";
import { useSeo } from "~/lib/seo/useSeo";
import { CAPA, DESTAQUE } from "~/lib/videos";
import { ProcessoDiagram } from "~/ui/ProcessoDiagram";
import { Revela } from "~/ui/Revela";
import { EntreNaLista } from "~/ui/EntreNaLista";
import { FaixaVideos, VideoDestaque } from "~/ui/FaixaVideos";

/** As fontes conferidas no vídeo mais recente (data centers nas urnas). */
const FONTES = [
  "Stateline",
  "Ballotpedia",
  "Fortune",
  "Journal-News",
  "Columbus Dispatch",
] as const;

const TRABALHO = [
  { to: "/build", label: "A carreira inteira, ano a ano" },
  { to: "/play", label: "Jogos: Magic: Puzzle Quest e Doodle Jump" },
  { to: "/engine", label: "Unity e Unreal" },
  { to: "/mobile", label: "Android nativo" },
  { to: "/react-native", label: "React Native" },
  { to: "/augment", label: "Realidade aumentada" },
  { to: "/ai", label: "Aplicações com modelos de linguagem" },
  { to: "/trade", label: "Fintech e automação" },
] as const;

/** O passo do escalonamento, em índices, para a entrada da capa. */
const i = (n: number) => ({ "--i": n }) as never;

export function Home() {
  useSeo({ pathname: "/" });

  return (
    <>
      {/* ── A promessa. Entra em quatro tempos, uma vez só. ─────── */}
      <section className="jg-shell pt-16 sm:pt-28">
        <div className="jg-grid">
          <div className="col-span-full lg:col-span-9">
            <p className="jg-eyebrow jg-entra" style={i(0)}>
              João Vidotto · engenheiro de software
            </p>
            <h1 className="jg-display jg-entra mt-8 text-balance" style={i(1)}>
              Inteligência artificial explicada{" "}
              <em className="jg-sublinha font-normal italic text-jg-accent">
                com fonte
              </em>
              .
            </h1>
          </div>

          <p
            className="jg-entra col-span-full mt-8 max-w-[52ch] text-[length:var(--text-step-0)] leading-[1.55] text-jg-fg-muted lg:col-span-7 lg:mt-12"
            style={i(2)}
          >
            Toda semana eu explico uma notícia ou um conceito de inteligência
            artificial num vídeo curto, conferido em duas fontes. Deixe seu
            e-mail e eu te mando o próximo, com a explicação escrita e os
            arquivos para você refazer o que viu.
          </p>

          {/* A captura fica aqui, acima da dobra, porque é o que o site
              precisa conseguir antes de ter qualquer coisa para vender. */}
          <div
            className="jg-entra col-span-full mt-10 lg:col-span-7"
            style={i(3)}
          >
            <EntreNaLista origem="home-topo" />
            <p className="mt-6 text-sm">
              <Link className="jg-link" to="/tutorial">
                ou comece pelos tutoriais, sem deixar e-mail
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* ── O trabalho. É ele que dá cor à página. ──────────────── */}
      <Revela className="mt-20 sm:mt-28" rotuladoPor="h-videos">
        <div className="jg-shell">
          <span className="jg-fio" />
          <div className="flex flex-wrap items-baseline justify-between gap-x-8 gap-y-2 pt-5">
            <h2 id="h-videos" className="jg-eyebrow" data-revela>
              Vídeos publicados
            </h2>
            <p className="text-sm text-jg-fg-muted" data-revela style={i(1)}>
              pixel art, 50 segundos, duas fontes cada ·{" "}
              <Link className="jg-link" to="/ia">
                ler por escrito
              </Link>
            </p>
          </div>

          {/* O destaque roda de verdade: capa parada não mostra animação,
              que é justamente o que está à venda aqui. */}
          <div className="jg-grid mt-9">
            <div
              className="col-span-8 sm:col-span-5 lg:col-span-4"
              data-revela
            >
              <VideoDestaque
                src={DESTAQUE.src}
                poster={CAPA(DESTAQUE.slug)}
                titulo={DESTAQUE.titulo}
              />
            </div>

            <div
              className="col-span-full mt-7 lg:col-span-6 lg:col-start-6 lg:mt-0 lg:self-center"
              data-revela
              style={i(1)}
            >
              <p className="jg-eyebrow">o mais recente</p>
              <h3 className="mt-4 font-display text-[length:var(--text-step-2)] font-medium leading-tight tracking-[-0.015em]">
                {DESTAQUE.titulo}
              </h3>
              <p className="mt-4 max-w-[46ch] leading-relaxed text-jg-fg-muted">
                {DESTAQUE.chamada}
              </p>
              <p className="mt-6 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm">
                <a className="jg-link" href="https://instagram.com/bittarg">
                  ver completo no Instagram
                </a>
                <Link className="jg-link" to="/tutorial/cabecudo">
                  como eu faço esses vídeos
                </Link>
              </p>
            </div>
          </div>
        </div>

        {/* A prateleira com o resto, andando sozinha. */}
        <div className="mt-16">
          <FaixaVideos pular={DESTAQUE.slug} />
        </div>
      </Revela>

      {/* ── Quem assina ─────────────────────────────────────────
          Sem contador de métrica: "12 vídeos" e "2 fontes" em corpo
          grande chamam atenção para o quanto é pouco. Credibilidade aqui
          vem de coisa verificável — um app com link, e os nomes das
          fontes que eu realmente conferi. */}
      <Revela className="jg-shell mt-24 sm:mt-32" rotuladoPor="h-quem">
        <span className="jg-fio" />
        <div className="jg-grid pt-9">
          <div className="col-span-full lg:col-span-6">
            <h2 id="h-quem" className="jg-eyebrow" data-revela>
              Quem assina
            </h2>
            <p
              className="mt-6 max-w-[46ch] text-[length:var(--text-step-0)] leading-[1.55]"
              data-revela
              style={i(1)}
            >
              João Vidotto, engenheiro de software há dez anos. Fui
              desenvolvedor Unity na Oktagon Games em{" "}
              <a
                className="jg-link"
                href="https://play.google.com/store/apps/details?id=com.d3p.olympic"
                target="_blank"
                rel="noopener noreferrer"
              >
                Magic: Puzzle Quest
              </a>
              , licenciado pela Wizards of the Coast, e em{" "}
              <a
                className="jg-link"
                href="https://m-apps.qoo-app.com/en-US/app/10205"
                target="_blank"
                rel="noopener noreferrer"
              >
                Doodle Jump Adventure
              </a>
              , da Lima Sky. Hoje sou engenheiro mobile sênior na{" "}
              <a
                className="jg-link"
                href="https://www.tonic3.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                Tonic3
              </a>
              , divisão de experiência da W3, em Dallas.
            </p>
            <p
              className="mt-5 max-w-[46ch] leading-relaxed text-jg-fg-muted"
              data-revela
              style={i(2)}
            >
              Não sou jornalista: sou quem constrói essas coisas. É por isso
              que eu confiro antes de publicar, e digo quando não sei.
            </p>
          </div>

          <div
            className="col-span-full mt-12 lg:col-span-5 lg:col-start-8 lg:mt-0"
            data-revela
            style={i(2)}
          >
            <p className="jg-eyebrow">Fontes do vídeo mais recente</p>
            <ul className="mt-5 text-sm">
              {FONTES.map((f) => (
                <li
                  key={f}
                  className="border-t border-jg-border py-2.5 first:border-t-0 first:pt-0"
                >
                  {f}
                </li>
              ))}
            </ul>
            <p className="mt-5 max-w-[34ch] text-sm leading-snug text-jg-fg-muted">
              Cada vídeo cita as suas na descrição. Quando um número não fecha
              em duas, ele não entra.
            </p>
          </div>
        </div>
      </Revela>

      {/* ── Como é feito ────────────────────────────────────────── */}
      <Revela className="jg-shell mt-24 sm:mt-32" rotuladoPor="h-processo">
        <span className="jg-fio" />
        <div className="jg-grid pt-9">
          <div className="col-span-full lg:col-span-6">
            <h2
              id="h-processo"
              className="font-display text-[length:var(--text-step-2)] font-medium leading-tight tracking-[-0.015em]"
              data-revela
            >
              Nenhum vídeo sai sem passar por duas fontes
            </h2>
            <p
              className="mt-6 max-w-[46ch] leading-relaxed text-jg-fg-muted"
              data-revela
              style={i(1)}
            >
              É por isso que demora, e é por isso que dá para confiar. Quando um
              número não fecha nas duas, ele fica de fora do vídeo — e eu digo
              isso na legenda. Quando os resultados ainda são parciais, a
              narração fala “até agora”.
            </p>
            <p
              className="mt-5 max-w-[46ch] leading-relaxed text-jg-fg-muted"
              data-revela
              style={i(2)}
            >
              A animação ao lado é o caminho inteiro: a notícia da semana, as
              duas fontes conferidas e a explicação de cinquenta segundos.
            </p>
          </div>

          <div
            className="col-span-full mt-12 lg:col-span-5 lg:col-start-8 lg:mt-0"
            data-revela
            style={i(2)}
          >
            <ProcessoDiagram />
          </div>
        </div>
      </Revela>

      {/* ── Tutoriais ───────────────────────────────────────────── */}
      <Revela className="jg-shell mt-24 sm:mt-32" rotuladoPor="h-tutoriais">
        <span className="jg-fio" />
        <div className="flex items-baseline justify-between gap-6 pt-5">
          <h2 id="h-tutoriais" className="jg-eyebrow" data-revela>
            Tutoriais
          </h2>
          <Link className="jg-link text-sm" to="/tutorial" data-revela style={i(1)}>
            todos
          </Link>
        </div>

        <ol className="mt-8">
          <li className="jg-rule-soft py-9" data-revela style={i(2)}>
            <article className="jg-grid">
              <p className="jg-eyebrow col-span-full lg:col-span-2">01</p>
              <div className="col-span-full lg:col-span-9">
                <h3 className="font-display text-[length:var(--text-step-1)] font-medium leading-snug">
                  <Link
                    className="underline decoration-jg-border decoration-1 underline-offset-[7px] transition-[text-decoration-color] duration-150 hover:decoration-jg-accent"
                    to="/tutorial/cabecudo"
                  >
                    Trocar o personagem de um vídeo com o Kling
                  </Link>
                </h3>
                <p className="mt-4 max-w-[58ch] leading-relaxed text-jg-fg-muted">
                  O passo a passo do antes e depois que eu publiquei: um vídeo
                  de nove segundos, duas imagens de referência e o prompt
                  pronto. Mostra o custo real em créditos e o que fazer quando
                  sai errado.
                </p>
                <p className="mt-4 text-sm text-jg-fg-muted">
                  Para iniciantes · imagens, prompt e roteiro para baixar
                </p>
              </div>
            </article>
          </li>
        </ol>

        <p className="jg-rule-soft py-7 text-sm text-jg-fg-muted">
          O próximo tutorial sai junto com o próximo vídeo.
        </p>
      </Revela>

      {/* ── Trabalho, discreto ──────────────────────────────────── */}
      <Revela className="jg-shell mt-24 sm:mt-32" rotuladoPor="h-trabalho">
        <span className="jg-fio" />
        <div className="jg-grid pt-9">
          <div className="col-span-full lg:col-span-6">
            <h2
              id="h-trabalho"
              className="font-display text-[length:var(--text-step-2)] font-medium leading-tight tracking-[-0.015em]"
              data-revela
            >
              Também construo software
            </h2>
            <p
              className="mt-6 max-w-[46ch] leading-relaxed text-jg-fg-muted"
              data-revela
              style={i(1)}
            >
              Dez anos entre jogos, Android nativo, realidade aumentada,
              aplicações com modelos de linguagem e automação de fintech. Se
              você precisa de alguém que entregue e explique o que fez,{" "}
              <Link className="jg-link" to="/hire">
                me conte o problema
              </Link>
              .
            </p>
          </div>

          <ul className="col-span-full mt-10 space-y-3 text-sm lg:col-span-5 lg:col-start-8 lg:mt-1">
            {TRABALHO.map((item, k) => (
              <li key={item.to} data-revela style={i(Math.min(k, 6))}>
                <Link
                  className="inline-block text-jg-fg-muted underline decoration-jg-border decoration-1 underline-offset-4 transition-colors duration-150 hover:text-jg-fg hover:decoration-jg-accent"
                  to={item.to}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </Revela>

      {/* ── A captura de novo, no fim ───────────────────────────────
          Quem leu até aqui é quem mais tende a deixar o e-mail, e já não
          tem o formulário do topo na tela. */}
      <Revela className="jg-shell mt-24 sm:mt-32" rotuladoPor="h-lista">
        <span className="jg-fio" />
        <div className="jg-grid pt-9">
          <div className="col-span-full lg:col-span-6">
            <h2
              id="h-lista"
              className="font-display text-[length:var(--text-step-2)] font-medium leading-tight tracking-[-0.015em]"
              data-revela
            >
              Receba o próximo por e-mail
            </h2>
            <p
              className="mt-6 max-w-[46ch] leading-relaxed text-jg-fg-muted"
              data-revela
              style={i(1)}
            >
              Um vídeo novo por semana, com a explicação escrita, as duas fontes
              que eu conferi e os arquivos quando tiver. Nada além disso.
            </p>
          </div>

          <div
            className="col-span-full mt-10 lg:col-span-5 lg:col-start-8 lg:mt-1"
            data-revela
            style={i(2)}
          >
            <EntreNaLista origem="home-rodape" />
          </div>
        </div>
      </Revela>
    </>
  );
}
